import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCreateAccount } from "@/hooks/use-accounts";
import { useCreateAccountInvite } from "@/hooks/use-account-portal-access";
import { useToast } from "@/hooks/use-toast";
import { AccountForm } from "@/components/accounts/account-form";
import { PageHeader, Breadcrumbs } from "@/components/ui";
import { ApiError } from "@njbsoft/api-client";

export const Route = createFileRoute("/_authenticated/accounts/new")({
  component: AccountNewPage,
});

function AccountNewPage() {
  const { t } = useTranslation(["accounts", "common"]);
  const navigate = useNavigate();
  const toasts = useToast();
  const createMutation = useCreateAccount();
  const inviteMutation = useCreateAccountInvite();
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [accountNumberError, setAccountNumberError] = useState<string | undefined>(undefined);

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Breadcrumbs
        items={[{ label: t("list.title"), to: "/accounts" }, { label: t("create.title") }]}
      />
      <PageHeader title={t("create.title")} />

      <div className="mt-6">
        <AccountForm
          isSubmitting={createMutation.isPending}
          submitError={submitError}
          accountNumberError={accountNumberError}
          onCancel={() => void navigate({ to: "/accounts" })}
          onSubmit={(body, opts) => {
            setSubmitError(undefined);
            setAccountNumberError(undefined);
            createMutation.mutate(body, {
              onSuccess: (account) => {
                const recipient = body.contact_email?.trim();
                const fireInvite = opts.invitePortal && recipient;
                const goToDetail = () =>
                  void navigate({ to: "/accounts/$id", params: { id: account.id } });
                if (fireInvite) {
                  inviteMutation.mutate(
                    { accountId: account.id, email: recipient, full_name: null },
                    {
                      onSuccess: () => {
                        toasts.success("accounts:create.invitePortalSent");
                        goToDetail();
                      },
                      // A failed invite never blocks navigation — the record
                      // exists and the invite can be re-sent from the card.
                      onError: () => {
                        toasts.error("accounts:errors.invitePortalFailed");
                        goToDetail();
                      },
                    },
                  );
                } else {
                  goToDetail();
                }
              },
              onError: (err) => {
                if (err instanceof ApiError && err.code === "account_number_conflict") {
                  setAccountNumberError(t("errors.accountNumberConflict"));
                  return;
                }
                const msg =
                  err instanceof ApiError
                    ? err.message
                    : err instanceof Error
                      ? err.message
                      : t("errors.createFailed");
                setSubmitError(msg);
              },
            });
          }}
        />
      </div>
    </div>
  );
}
